import React from "react";
import { useLanguage } from "@/App";
import { TrendingUp, Euro, Users, Globe, Award, Zap } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";

const AnimatedNumber = ({ value, suffix = "", prefix = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const num = parseInt(value.replace(/[^0-9]/g, ""));
    if (isNaN(num)) return;

    const duration = 2000;
    const steps = 60;
    const increment = num / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= num) {
        setCount(num);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isInView, value]);

  return (
    <span ref={ref}>
      {prefix}{count.toLocaleString("nl-NL")}{suffix}
    </span>
  );
};

const ResultsStats = () => {
  const { language } = useLanguage();

  const stats = [
    {
      icon: Euro,
      value: "2000000",
      prefix: "€",
      suffix: "+",
      display: "€2M+",
      label_en: "Revenue generated for clients",
      label_nl: "Omzet gegenereerd voor klanten",
      color: "text-[#FF6B00]"
    },
    {
      icon: Users,
      value: "150",
      prefix: "",
      suffix: "+",
      display: "150+",
      label_en: "Happy clients served",
      label_nl: "Tevreden klanten geholpen",
      color: "text-purple-400"
    },
    {
      icon: Globe,
      value: "500000",
      prefix: "",
      suffix: "+",
      display: "500K+",
      label_en: "Website visitors per month",
      label_nl: "Website bezoekers per maand",
      color: "text-blue-400"
    },
    {
      icon: TrendingUp,
      value: "250",
      prefix: "",
      suffix: "%",
      display: "250%",
      label_en: "Average ROI for our clients",
      label_nl: "Gemiddelde ROI voor onze klanten",
      color: "text-green-400"
    },
    {
      icon: Award,
      value: "98",
      prefix: "",
      suffix: "%",
      display: "98%",
      label_en: "Client satisfaction rate",
      label_nl: "Klanttevredenheidspercentage",
      color: "text-amber-400"
    },
    {
      icon: Zap,
      value: "10000",
      prefix: "",
      suffix: "+",
      display: "10K+",
      label_en: "Hours of development delivered",
      label_nl: "Uren development opgeleverd",
      color: "text-cyan-400"
    }
  ];

  return (
    <section
      data-testid="results-stats-section"
      className="py-32 bg-[#0A0A0A] relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,107,0,0.08),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(139,92,246,0.05),transparent_50%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="inline-flex items-center gap-2 bg-[#FF6B00]/10 border border-[#FF6B00]/20 px-5 py-2 mb-6">
            <TrendingUp size={16} className="text-[#FF6B00]" />
            <span className="text-[#FF6B00] text-xs font-bold uppercase tracking-[0.2em]">
              {language === "nl" ? "Onze Resultaten" : "Our Results"}
            </span>
          </span>
          <h2
            data-testid="results-stats-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {language === "nl" ? "Cijfers Die " : "Numbers That "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-[#FF4500]">
              {language === "nl" ? "Spreken" : "Speak"}
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {language === "nl"
              ? "Geen loze beloftes, maar bewezen resultaten. Dit is wat wij voor onze klanten hebben bereikt."
              : "No empty promises, just proven results. This is what we've achieved for our clients."}
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              data-testid={`result-stat-${index}`}
              className="bg-[#0A0A0A] p-8 md:p-12 text-center group hover:bg-[#0f0f0f] transition-all"
            >
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 bg-white/5 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <stat.icon size={28} className={stat.color} />
                </div>
              </div>
              <p
                className={`text-4xl md:text-5xl lg:text-6xl font-black ${stat.color} mb-3`}
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {stat.display}
              </p>
              <p className="text-zinc-400 text-sm md:text-base">
                {language === "nl" ? stat.label_nl : stat.label_en}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ResultsStats;
